import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Popover,
  Avatar
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { usePersonaLibrary } from '../../../hooks/resources/usePersonaLibrary';

const PersonaPicker = ({
  value,
  onChange,
  label = 'Select Persona',
  required = false,
  helperText = '',
  error = false
}) => {
  const { personas, loading, error: fetchError } = usePersonaLibrary({ isActive: true });
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [infoAnchor, setInfoAnchor] = useState(null);

  useEffect(() => {
    if (value && personas.length > 0) {
      const persona = personas.find(p => p._id === value);
      setSelectedPersona(persona);
    } else if (!value) {
      setSelectedPersona(null);
    }
  }, [value, personas]);

  const handleChange = (event, newValue) => {
    setSelectedPersona(newValue);
    onChange(newValue?._id || null);
  };

  const handleInfoClick = (event) => {
    event.stopPropagation();
    setInfoAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchor(null);
  };

  return (
    <Box>
      <Autocomplete
        value={selectedPersona}
        onChange={handleChange}
        options={personas || []}
        getOptionLabel={(option) => option?.name || ''}
        loading={loading}
        isOptionEqualToValue={(option, value) => option?._id === value?._id}
        componentsProps={{
          popper: {
            sx: { zIndex: 1500 }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error || !!fetchError}
            helperText={fetchError?.message || helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {selectedPersona && (
                    <IconButton size="small" onClick={handleInfoClick}>
                      <Info fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {option.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.specialization}
                </Typography>
              </Box>
              {option.usageCount?.flows > 0 && (
                <Chip
                  size="small"
                  label={`${option.usageCount.flows} flows`}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          );
        }}
      />

      <Popover
        open={Boolean(infoAnchor)}
        anchorEl={infoAnchor}
        onClose={handleInfoClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedPersona && (
          <Box sx={{ p: 2, maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>
              {selectedPersona.name}
            </Typography>

            <Typography variant="body2" paragraph>
              {selectedPersona.description}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                <strong>Specialization:</strong> {selectedPersona.specialization}
              </Typography>
              {selectedPersona.modelConfig && (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Model:</strong> {selectedPersona.modelConfig.provider} ({selectedPersona.modelConfig.model})
                </Typography>
              )}
              {selectedPersona.usageCount && (
                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Usage:</strong> {selectedPersona.usageCount.flows} flows
                  {selectedPersona.usageCount.strategies && `, ${selectedPersona.usageCount.strategies} strategies`}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default PersonaPicker;
